import { isNotNullish } from 'effect/Predicate'

import type { CoreModel } from '../../../../schemas/model/model.ts'
import type { ModelProperty } from '../../../../schemas/model/modelProperty.ts'
import type { ViewQueryNode } from '../../../../schemas/query/index.ts'
import type { ViewConfigSheet, ViewConfigTreeNode } from '../../../../schemas/view_config/index.ts'
import type { CompileContext } from '../../common.ts'
import { generateSearchSql } from './searchSql/generateSearchSql.ts'
import { generateTableSql } from './tableSql/generateTableSql.ts'

export function compileSheetViewConfigTree(
  tree: ViewConfigSheet['tree'],
  context: CompileContext,
): ViewQueryNode | undefined {
  if (tree === undefined) {
    return undefined
  }

  return rec(tree, undefined, undefined, context)
}

// eslint-disable-next-line complexity
function rec(
  node: ViewConfigTreeNode,
  parentQueryNode: ViewQueryNode | undefined,
  parentModel: CoreModel | undefined,
  context: CompileContext,
): ViewQueryNode | undefined {
  const model = context.modelSearcher.searchModel(node.modelName)
  if (model === undefined) {
    return undefined
  }
  const { name } = node
  const referenceToProperty = context.modelSearcher.searchProperty(
    node.referenceToProperty?.modelName ?? '',
    node.referenceToProperty?.propertyName ?? '',
  )
  const referenceLabel = referenceToProperty?.label
  const label =
    node.ui?.label ??
    `${model.label}${referenceLabel !== undefined && referenceLabel !== '' ? `(${referenceLabel})` : ``}`

  // node.override.tableSql の取得（未定義なら新規生成）
  const generatedTableSql = generateTableSql(node, context)
  const queryNodeWithoutJoin: ViewQueryNode = {
    name,
    path: [...(parentQueryNode?.path ?? []), name],
    read:
      generatedTableSql === undefined
        ? { tableSql: model.name, tableSqlTransparency: 'transparent', tableType: 'table', idColumn: 'id' }
        : {
            tableSql: generatedTableSql.tableSql,
            tableSqlTransparency: generatedTableSql.tableSqlTransparency,
            tableType: node.override?.tableType ?? 'subquery',
            idColumn: 'id',
          },
    write:
      model.creatable === false
        ? undefined
        : {
            streamName: model.name,
            // eslint-disable-next-line sonarjs/todo-tag
            // 初期設計時の「parentId」という名前が非常に微妙になってしまったが、やりたいことは以下。one_to_manyのときしか使わない TODO: 改修したい
            parentIdColumn: referenceToProperty?.write?.sourcePropertyName,
          },
    meta: {
      label,
      dependedStreamNames: [model.name], // deprecated?
    },
  }

  const joinResult =
    parentQueryNode === undefined || parentModel === undefined
      ? undefined
      : generateJoin(node, parentQueryNode, parentModel, model, queryNodeWithoutJoin, context)
  const join = joinResult?.join

  if (join === undefined && parentModel !== undefined) {
    context.logs.error(`JOINの生成に失敗しました。`)
    return undefined
  }

  const queryNode: ViewQueryNode = {
    ...queryNodeWithoutJoin,
    read: {
      ...queryNodeWithoutJoin.read,
      join,
    },
  }
  const children = (node.children ?? []).map((child) => rec(child, queryNode, model, context)).filter(isNotNullish)

  return {
    ...queryNode,
    children: children.length > 0 ? children : undefined,
  }
}

// eslint-disable-next-line @typescript-eslint/max-params
function generateJoin(
  node: ViewConfigTreeNode,
  parentQueryNode: ViewQueryNode,
  parentModel: CoreModel,
  model: CoreModel,
  queryNode: ViewQueryNode,
  context: CompileContext,
): { join: ViewQueryNode['read']['join']; referenceToProperty: ModelProperty } | undefined {
  if (node.referenceToProperty === undefined) {
    // return undefined
    // eslint-disable-next-line sonarjs/todo-tag
    throw new Error(`子ノードでreferenceToが指定されていません`) // TODO: ありえないはず
  }

  const referenceToProperty = context.modelSearcher.searchProperty(
    node.referenceToProperty.modelName,
    node.referenceToProperty.propertyName,
  )
  if (referenceToProperty === undefined) {
    context.logs.error(
      `参照プロパティが見つかりません。モデル名: ${node.referenceToProperty.modelName}, プロパティ名: ${node.referenceToProperty.propertyName}`,
    )
    return undefined
  }

  const { relation, parentNodeColumnName, currentNodeColumnName, parentNodeColumnLabel, currentNodeColumnLabel } =
    node.name === node.referenceToProperty.nodeName
      ? {
          // propertyをこのノードが持っている場合、すなわち親を参照している場合
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: one_to_oneの判定が雑
          relation: (referenceToProperty.referenceTo ?? []).some((x) => x.relation === 'one_to_one')
            ? ('one_to_one' as const)
            : (`one_to_many` as const),
          parentNodeColumnName: node.referenceToProperty.referenceTo.key ?? 'id',
          parentNodeColumnLabel: 'ID',
          currentNodeColumnName: referenceToProperty.name,
          currentNodeColumnLabel: referenceToProperty.label,
        }
      : {
          relation: `many_to_one` as const,
          currentNodeColumnName: node.referenceToProperty.referenceTo.key ?? 'id',
          currentNodeColumnLabel: 'ID',
          parentNodeColumnName: referenceToProperty.name,
          parentNodeColumnLabel: referenceToProperty.label,
        }

  const search = generateSearchSql({
    model,
    parentModel,
    parentNode: parentQueryNode,
    context,
  })
  const joinType = node.override?.joinType ?? `LEFT JOIN`
  const join: ViewQueryNode['read']['join'] = {
    relation,
    joinType,
    joinOn:
      node.override?.joinType === 'LEFT JOIN LATERAL'
        ? {
            type: 'sql',
            sql: 'TRUE',
          }
        : // eslint-disable-next-line sonarjs/no-nested-conditional
          node.referenceToProperty.override?.onSql === undefined
          ? {
              type: 'id',
              parentNodeName: parentQueryNode.name,
              parentNodeColumnName,
              currentNodeName: node.name,
              currentNodeColumnName,
              meta: {
                parentNodeTableSql: parentModel.name,
                parentNodeLabel: parentModel.label,
                parentNodeColumnLabel,
                parentNodeStreamName: parentModel.name,
                parentNodePropertyName: parentNodeColumnName,
                currentNodeTableSql: model.name,
                currentNodeLabel: model.label,
                currentNodeColumnLabel,
                currentNodeStreamName: model.name,
                currentNodePropertyName: currentNodeColumnName,
              },
            }
          : {
              type: 'sql',
              sql: node.referenceToProperty.override.onSql,
            },
    // node.additionalJoinOn は with句で処理しないとブロックフィルタの整合性が取れないため、joinFilterTree: undefined で処理する
    joinFilterTree: undefined,
    search: {
      ...search,
      sql: node.override?.searchSql ?? search.sql,
    },
  }

  return {
    join,
    referenceToProperty,
  }
}
